import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2 } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Checkbox } from "../ui/checkbox";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const VAZIO = {
	name: "",
	url: "",
	method: "POST",
	events: [],
	isActive: true,
	rateLimitPerMinute: 120,
	authToken: "",
};

/**
 * Cadastro e edição de um webhook.
 *
 * Editar NÃO mexe em credencial: segredo e token entram pelo botão de
 * rotacionar, que é ação própria, auditada e com janela em que o valor
 * anterior continua valendo. Deixá-los aqui faria um salvamento distraído
 * derrubar a integração sem ninguém entender por quê.
 */
const WebhookFormModal = ({ aberto, aoFechar, webhook, aoSalvar }) => {
	const [dados, setDados] = useState(VAZIO);
	const [catalogo, setCatalogo] = useState([]);
	const [salvando, setSalvando] = useState(false);

	const editando = Boolean(webhook?.id);

	const carregarCatalogo = useCallback(async () => {
		try {
			// O catálogo vem do servidor, nunca de uma lista escrita aqui: dois
			// catálogos divergem na primeira mudança, e a tela passaria a oferecer
			// evento que não dispara.
			const { data } = await api.get("/webhooks/events");
			setCatalogo(data.events || []);
		} catch (err) {
			toastError(err);
		}
	}, []);

	useEffect(() => {
		if (!aberto) return;
		carregarCatalogo();
		setDados(
			webhook
				? {
						name: webhook.name || "",
						url: webhook.url || "",
						method: webhook.method || "POST",
						events: webhook.events || [],
						isActive: webhook.isActive !== false,
						rateLimitPerMinute: webhook.rateLimitPerMinute ?? 120,
						authToken: "",
					}
				: VAZIO,
		);
	}, [aberto, webhook, carregarCatalogo]);

	const alternarEvento = (chave) =>
		setDados((atual) => ({
			...atual,
			events: atual.events.includes(chave)
				? atual.events.filter((e) => e !== chave)
				: [...atual.events, chave],
		}));

	const salvar = async (e) => {
		e.preventDefault();

		if (!dados.events.length) {
			toast.error("Escolha ao menos um evento, senão nada será enviado.");
			return;
		}

		setSalvando(true);
		try {
			const corpo = {
				name: dados.name,
				url: dados.url,
				method: dados.method,
				events: dados.events,
				isActive: dados.isActive,
				rateLimitPerMinute: Number(dados.rateLimitPerMinute) || 120,
			};

			if (editando) {
				const { data } = await api.put(`/webhooks/${webhook.id}`, corpo);
				aoSalvar(data, null);
			} else {
				const { data } = await api.post("/webhooks", {
					...corpo,
					...(dados.authToken ? { authToken: dados.authToken } : {}),
				});
				// O segredo volta inteiro só aqui. Quem chamou mostra e avisa.
				aoSalvar(data, data.secret);
			}

			aoFechar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const grupos = catalogo.reduce((acc, evento) => {
		acc[evento.grupo] = [...(acc[evento.grupo] || []), evento];
		return acc;
	}, {});

	return (
		<Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>
						{editando ? "Editar webhook" : "Novo webhook"}
					</DialogTitle>
					<DialogDescription>
						Para onde o sistema avisa quando algo acontece aqui dentro.
					</DialogDescription>
				</DialogHeader>

				<form onSubmit={salvar} className="space-y-4">
					<div className="grid gap-3 sm:grid-cols-2">
						<div>
							<Label htmlFor="name">Nome</Label>
							<Input
								id="name"
								required
								minLength={2}
								placeholder="n8n do financeiro"
								value={dados.name}
								onChange={(e) => setDados({ ...dados, name: e.target.value })}
							/>
						</div>

						<div>
							<Label htmlFor="method">Método</Label>
							<Select
								value={dados.method}
								onValueChange={(v) => setDados({ ...dados, method: v })}
							>
								<SelectTrigger id="method">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{["POST", "PUT", "PATCH"].map((m) => (
										<SelectItem key={m} value={m}>
											{m}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>

					<div>
						<Label htmlFor="url">URL de destino</Label>
						<Input
							id="url"
							required
							type="url"
							placeholder="https://seu-sistema.com.br/webhook"
							value={dados.url}
							onChange={(e) => setDados({ ...dados, url: e.target.value })}
						/>
						<p className="mt-1 text-xs text-muted-foreground">
							Precisa ser HTTPS e público. Endereço de rede interna é recusado:
							um webhook apontando para dentro vira porta para varrer a rede.
						</p>
					</div>

					<div>
						<Label>Eventos</Label>
						<p className="mb-2 text-xs text-muted-foreground">
							Só o que estiver marcado é enviado.
						</p>

						<div className="space-y-3 rounded-lg border p-3">
							{Object.entries(grupos).map(([grupo, eventos]) => (
								<div key={grupo}>
									<p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
										{grupo}
									</p>
									<div className="grid gap-2 sm:grid-cols-2">
										{eventos.map((evento) => (
											<label
												key={evento.chave}
												className="flex cursor-pointer items-center gap-2 text-sm"
											>
												<Checkbox
													checked={dados.events.includes(evento.chave)}
													onCheckedChange={() => alternarEvento(evento.chave)}
												/>
												<span>{evento.rotulo}</span>
												<span className="font-mono text-[11px] text-muted-foreground">
													{evento.chave}
												</span>
											</label>
										))}
									</div>
								</div>
							))}

							{!catalogo.length && (
								<p className="text-sm text-muted-foreground">
									Carregando eventos…
								</p>
							)}
						</div>
					</div>

					<div className="grid gap-3 sm:grid-cols-2">
						<div>
							<Label htmlFor="rate">Limite por minuto</Label>
							<Input
								id="rate"
								type="number"
								min={0}
								value={dados.rateLimitPerMinute}
								onChange={(e) =>
									setDados({ ...dados, rateLimitPerMinute: e.target.value })
								}
							/>
							<p className="mt-1 text-xs text-muted-foreground">
								Acima disso a entrega é adiada, nunca descartada. Zero é sem
								limite.
							</p>
						</div>

						{!editando && (
							<div>
								<Label htmlFor="authToken">Bearer token do destino</Label>
								<Input
									id="authToken"
									type="password"
									autoComplete="off"
									placeholder="Opcional"
									value={dados.authToken}
									onChange={(e) =>
										setDados({ ...dados, authToken: e.target.value })
									}
								/>
								<p className="mt-1 text-xs text-muted-foreground">
									Só se o seu destino exigir. É credencial separada da
									assinatura.
								</p>
							</div>
						)}
					</div>

					<label className="flex cursor-pointer items-center gap-2 text-sm">
						<Switch
							checked={dados.isActive}
							onCheckedChange={(v) => setDados({ ...dados, isActive: v })}
						/>
						Ativo
					</label>

					<DialogFooter>
						<Button type="button" variant="outline" onClick={aoFechar}>
							Cancelar
						</Button>
						<Button type="submit" disabled={salvando}>
							{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
							{editando ? "Salvar" : "Criar webhook"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default WebhookFormModal;
